/**
 * Forgot password page component - shadcn/ui
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { LanguageSelector } from '../../components/ui/language-selector';

import { authApi, AuthApiError } from '../../shared/auth';
import { getGenericErrorMessage } from "@/shared/utils/errors";
import { STANDALONE_TITLE } from '../../shared/styles/typography';

interface LocationState {
  email?: string;
}

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const [email, setEmail] = useState(locationState?.email || '');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const errors: Record<string, string> = {};
    if (!email.trim()) {
      errors.email = t('validation.emailRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      await authApi.forgotPassword({ email: email.trim().toLowerCase() });
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.details) setFieldErrors(err.details);
        setError(err.message);
      } else {
        setError(getGenericErrorMessage());
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-[400px] w-full">
          <CardContent className="p-8">
            <div className="flex justify-end mb-2">
              <LanguageSelector variant="labeled" />
            </div>
            <div className="flex justify-center mb-2">
              <img src="/panluma-logo.png" alt={tCommon('app.name')} className="h-24" />
            </div>
            <h2 className="text-xl font-semibold text-center mb-4">{t('forgotPassword.checkEmail')}</h2>
            <Alert className="mb-6 border-green-200 bg-green-50 text-green-800">
              <AlertDescription>{t('forgotPassword.successMessage')}</AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground text-center mb-6">{t('forgotPassword.checkSpam')}</p>
            <Button asChild className="w-full" size="lg">
              <Link to="/login">{t('forgotPassword.backToLogin')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-[400px] w-full">
        <CardContent className="p-8">
          <div className="flex justify-end mb-2">
            <LanguageSelector variant="labeled" />
          </div>
          <h1 className={`${STANDALONE_TITLE} text-center mb-2`}>{tCommon('app.name')}</h1>
          <h2 className="text-xl font-semibold text-center mb-2">{t('forgotPassword.title')}</h2>
          <p className="text-sm text-muted-foreground text-center mb-6">{t('forgotPassword.description')}</p>

          {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isLoading} autoComplete="email" autoFocus className={fieldErrors.email ? 'border-destructive' : ''} />
                {fieldErrors.email && <p className="text-sm text-destructive">{fieldErrors.email}</p>}
              </div>

              <Button type="submit" className="w-full mt-6" size="lg" disabled={isLoading}>
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('forgotPassword.submit')}
              </Button>

              <p className="text-sm text-center">
                {t('forgotPassword.rememberPassword')}{' '}
                <Link to="/login" className="text-primary hover:underline">{t('forgotPassword.backToLogin')}</Link>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
